import axios from 'axios';

/**
 * Creates handlers that translate AppBridge requests to Surface API calls.
 *
 * Since cx-portal uses the Surface API as an MCP proxy (no direct MCP client access),
 * we need to manually implement handlers that call the Surface API endpoints.
 *
 * @param {string} surfaceUrl - Base URL for the Surface API
 * @param {string} accessToken - Bearer token for authentication
 * @param {string} cid - Client ID for the current client
 * @returns {Object} Handler functions for AppBridge
 */
export function createSurfaceApiHandlers(surfaceUrl, accessToken, cid) {
  const headers = {
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json'
  };

  return {
    /**
     * Read a resource from the MCP server via Surface API.
     * Maps to: POST /v1/genai/aquabot/resource
     *
     * @param {Object} params - Request parameters
     * @param {string} params.uri - Resource URI to fetch
     * @returns {Promise<Object>} Resource contents in MCP format
     */
    async readResource(params) {
      const response = await axios.post(
        `${surfaceUrl}/v1/genai/aquabot/resource`,
        { uri: params.uri, cid },
        { headers, timeout: 60000 }
      );

      // Normalize response to MCP ReadResourceResult format
      const text = response.data.text || response.data.content ||
        (typeof response.data === 'string' ? response.data : JSON.stringify(response.data));

      return {
        contents: [{
          uri: params.uri,
          mimeType: 'text/html;profile=mcp-app',
          text
        }]
      };
    },

    /**
     * Call a tool on the MCP server via Surface API.
     * Maps to: POST /v1/genai/aquabot/tool
     *
     * @param {Object} params - Request parameters
     * @param {string} params.name - Tool name
     * @param {Object} params.arguments - Tool arguments
     * @returns {Promise<Object>} Tool call result
     */
    async callTool(params) {
      const response = await axios.post(
        `${surfaceUrl}/v1/genai/aquabot/tool`,
        { name: params.name, arguments: params.arguments || {}, cid },
        { headers, timeout: 120000 }
      );

      return response.data;
    }
  };
}

/**
 * Fetch the list of available MCP tools with their metadata.
 * Tools with _meta.ui.resourceUri have associated UI visualizations.
 *
 * @param {string} surfaceUrl - Base URL for the Surface API
 * @param {string} accessToken - Bearer token for authentication
 * @returns {Promise<Object>} Object with tools array and toolsWithUi array
 */
export async function fetchMcpTools(surfaceUrl, accessToken) {
  const response = await axios.get(
    `${surfaceUrl}/v1/genai/aquabot/tools`,
    {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      timeout: 30000
    }
  );

  return response.data;
}

/**
 * Extract UI resource URI from tool metadata.
 * Checks both nested format (_meta.ui.resourceUri) and flat format (_meta["ui/resourceUri"]).
 *
 * @param {Object} tool - Tool object with potential _meta property
 * @returns {string|null} UI resource URI or null if not present
 */
export function getToolUiResourceUri(tool) {
  if (!tool || !tool._meta) {
    return null;
  }

  // Try nested format first: _meta.ui.resourceUri
  if (tool._meta.ui && tool._meta.ui.resourceUri) {
    const uri = tool._meta.ui.resourceUri;
    if (typeof uri === 'string' && uri.startsWith('ui://')) {
      return uri;
    }
  }

  // Try flat format: _meta["ui/resourceUri"]
  const flatUri = tool._meta['ui/resourceUri'];
  if (typeof flatUri === 'string' && flatUri.startsWith('ui://')) {
    return flatUri;
  }

  return null;
}
